// ───────────────────────────────────────────────
// Auth context. Holds the signed-in user, the archers they can act for,
// and the currently active archer. Hydrates once from GET /api/auth/me so
// auth state survives a page reload, and wires the API client's 401 handler
// so an expired/absent session logs the user out and routes to /login.
// ───────────────────────────────────────────────
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  api,
  setActiveArcherId as syncActiveArcher,
  setUnauthorizedHandler,
} from "./api";
import { safeLocalStorageGet, safeLocalStorageSet } from "./storage";

// The active archer is remembered across reloads so a coach/parent stays in
// the workspace they last used. The switcher (#89) writes this key too.
const ACTIVE_ARCHER_KEY = "xring.activeArcherId";

// Roles that can write scoring data (add ends, edit settings, etc.).
// Mirrors backend/app/security.py WRITE_ROLES = {full, score_on_behalf}.
export const WRITE_ROLES = ["full", "score_on_behalf"];

// Public auth routes that must stay reachable while logged out. A 401 from the
// mount-time /api/auth/me hydration clears auth state but must NOT redirect
// away from these — otherwise a logged-out deep-link bounces to /login (#1298).
const PUBLIC_NO_REDIRECT_PATHS = new Set([
  "/signup",
  "/forgot-password",
  "/terms",
]);

const AuthContext = createContext(null);

function readStoredArcherId() {
  if (typeof window === "undefined") return null;
  // A blocked store (incognito / SecurityError) reads as "no remembered
  // archer" — safeLocalStorageGet swallows the throw (#2028).
  const raw = safeLocalStorageGet(ACTIVE_ARCHER_KEY);
  return raw ? Number(raw) : null;
}

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Track the current pathname in a ref so the (long-lived) 401 handler reads
  // the live path at fire-time rather than a stale closure value.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const [user, setUser] = useState(null);
  const [archers, setArchers] = useState([]); // [{ archer, role }]
  const [activeArcherId, setActiveArcherId] = useState(readStoredArcherId);
  // Feature flags resolved for this account by GET /api/auth/me (#2452). The
  // SPA gates whole modules on these — see pages/rbte — so a flagged-off
  // account renders an empty state instead of discovering the 403 itself.
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true); // true until first hydration settles

  const applyAuth = useCallback((data) => {
    setUser(data.user);
    // One choke point for every path that establishes auth state — mount-time
    // hydration, login, signup, and invitation accept all funnel through here,
    // so `features` cannot go stale on one of them. A payload without the field
    // (an older backend) reads as "no features", never as undefined.
    setFeatures(data.features ?? []);
    const memberships = data.archers || [];
    setArchers(memberships);
    // Keep the API layer's X-Active-Archer header in step with the archer
    // the `activeArcher` memo below will resolve to.
    const ids = memberships.map((m) => m.archer.id);
    const stored = readStoredArcherId();
    syncActiveArcher(ids.includes(stored) ? stored : ids[0] ?? null);
  }, []);

  const clearAuth = useCallback(() => {
    setUser(null);
    setArchers([]);
    setFeatures([]);
    syncActiveArcher(null);
  }, []);

  // Pull the current session's user + archers. Returns the user, or null
  // when there is no valid session.
  const refresh = useCallback(async () => {
    try {
      const data = await api.authMe();
      applyAuth(data);
      return data.user;
    } catch {
      clearAuth();
      return null;
    }
  }, [applyAuth, clearAuth]);

  const login = useCallback(
    async (email, password) => {
      const data = await api.login({ email, password });
      applyAuth(data);
      return data.user;
    },
    [applyAuth],
  );

  // Signup creates the account, its archer, and a session in one call — the
  // response is an AuthMeOut, so the user is logged in immediately.
  const signup = useCallback(
    async ({ email, password, name, invite_code }) => {
      const data = await api.signup({ email, password, name, invite_code });
      applyAuth(data);
      return data.user;
    },
    [applyAuth],
  );

  // Adopt an auth state returned by a flow that already established a session
  // server-side (accepting an archer invitation). Avoids a second /me round-trip.
  const acceptAuth = useCallback(
    (data) => {
      applyAuth(data);
      return data.user;
    },
    [applyAuth],
  );

  const logout = useCallback(async () => {
    // Best-effort server revocation; clear locally even if the call fails.
    try {
      await api.logout();
    } catch {
      /* ignore — the cookie is cleared server-side or already invalid */
    }
    clearAuth();
    navigate("/login");
  }, [clearAuth, navigate]);

  const selectArcher = useCallback((archerId) => {
    setActiveArcherId(archerId);
    // Update the API header synchronously so a scoped reload triggered by
    // this switch fetches with the new archer already in effect.
    syncActiveArcher(archerId);
    // Private mode / storage disabled — in-memory selection still works.
    safeLocalStorageSet(ACTIVE_ARCHER_KEY, String(archerId));
  }, []);

  // Initial hydration — runs once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.authMe();
        if (!cancelled) applyAuth(data);
      } catch {
        if (!cancelled) clearAuth();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyAuth, clearAuth]);

  // Centralized 401 handling. A 401 from any API call clears auth state and
  // routes to /login. Network errors never reach this handler, so a dropped
  // connection — e.g. mid-round on mobile — cannot force a logout.
  //
  // Exception (#1298): the public auth routes must stay reachable while logged
  // out. The mount-time GET /api/auth/me 401s for an anonymous visitor, which
  // would otherwise bounce a deep-link to /signup, /forgot-password, or /terms
  // straight to /login. On those paths we still clear auth state but do not
  // redirect — /terms in particular is linked from signup and must be public.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearAuth();
      if (!PUBLIC_NO_REDIRECT_PATHS.has(pathnameRef.current)) {
        navigate("/login");
      }
    });
    return () => setUnauthorizedHandler(null);
  }, [clearAuth, navigate]);

  // Resolve the active archer from its stored id, falling back to the first
  // accessible archer.
  const activeArcher = useMemo(() => {
    if (archers.length === 0) return null;
    const match = archers.find((m) => m.archer.id === activeArcherId);
    return (match || archers[0]).archer;
  }, [archers, activeArcherId]);

  const hasFeature = useCallback((name) => features.includes(name), [features]);

  const value = useMemo(
    () => ({
      user,
      archers,
      activeArcher,
      isAuthenticated: user != null,
      features,
      hasFeature,
      loading,
      login,
      signup,
      acceptAuth,
      logout,
      refresh,
      selectArcher,
    }),
    [user, archers, activeArcher, features, hasFeature, loading, login, signup, acceptAuth, logout, refresh, selectArcher],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx == null) {
    throw new Error("useAuth must be used within an <AuthProvider>");
  }
  return ctx;
}
